import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { forkJoin } from "rxjs/observable/forkJoin";

import { GlobalVariable } from "../../global";
import {
  FieldRequestParameters,
  GroupByRequestParameters,
  OrderByRequestParameters,
  RequestParameters,
} from "../../shared/models/request-parameters/parameter.model";
import { DatePipe } from "@angular/common";

@Injectable()
export class ReportsService {
  reportsSack: any;
  reportParameters: any;
  globalReportSack: any;
  globalReportParameters: any;

  constructor(private http: HttpClient) {}

  getReport(parameters: any) {
    this.reportParameters = parameters;
    const params = this.normalizeReportParameters(parameters);

    return this.http
      .get(GlobalVariable.BASE_API_URL + "/report/operations", {
        params: params.getHttpParams(),
      })
      .map((response: any) => {
        this.reportsSack = response;
        return response;
      });
  }

  getDailyReportByCountry(parameters: any) {
    const params = this.normalizeReportParameters(parameters);

    return this.http
      .get(GlobalVariable.BASE_API_URL + "/report/operations", {
        params: params.getHttpParams(),
      })
      .map((response: any) => {
        this.reportsSack = response;
        return response;
      });
  }

  getGlobalReport(parameters: any) {
    this.globalReportParameters = parameters;
    const url = GlobalVariable.BASE_API_URL + "/report/consolide";
    const st = parameters.fields && parameters.fields.status;
    if (
      st != null &&
      typeof st === "object" &&
      !Array.isArray(st) &&
      st.value === "ALL_CANCELLED_STATUSES"
    ) {
      const cloneWithStatus = (statusValue: string) => {
        const p = JSON.parse(JSON.stringify(parameters));
        p.fields = Object.assign({}, p.fields, {
          status: { field: "CANCELED", value: statusValue },
        });
        return p;
      };
      const pCancelOp = cloneWithStatus("CANCEL_OPERATION");
      const pCanceled = cloneWithStatus("CANCELED");
      return forkJoin([
        this.http.get(url, {
          params: this.normalizeReportParameters(pCancelOp).getHttpParams(),
        }),
        this.http.get(url, {
          params: this.normalizeReportParameters(pCanceled).getHttpParams(),
        }),
      ]).map((responses: any[]) => {
        return this.mergeGlobalReportCancelledResponses(responses, parameters);
      });
    }

    const params = this.normalizeReportParameters(parameters);

    return this.http
      .get(url, {
        params: params.getHttpParams(),
      })
      .map((response: any) => {
        const rawList =
          response != null
            ? response.result != null
              ? response.result
              : response.results
            : null;
        const list = Array.isArray(rawList) ? rawList : [];
        const apiParams = response && response.parameters;
        const fallbackParams = {
          fields:
            parameters && parameters.fields ? parameters.fields : {},
          groupBy:
            parameters && parameters.groupBy ? parameters.groupBy : [],
        };
        const merged = Object.assign({}, response, {
          result: list,
          results: list,
          parameters:
            apiParams != null ? apiParams : fallbackParams,
        });
        this.globalReportSack = merged;
        return merged;
      });
  }

  private extractGlobalReportRowList(response: any): any[] {
    if (response == null) {
      return [];
    }
    const raw =
      response.result != null ? response.result : response.results;
    return Array.isArray(raw) ? raw : [];
  }

  /**
   * Deux appels (CANCEL_OPERATION et CANCELED) car plusieurs critères status
   * dans une même requête sont souvent combinés en ET côté API.
   */
  private mergeGlobalReportCancelledResponses(
    responses: any[],
    parameters: any,
  ): any {
    const seen: { [key: string]: boolean } = {};
    const merged: any[] = [];
    for (let r = 0; r < responses.length; r++) {
      const list = this.extractGlobalReportRowList(responses[r]);
      for (let i = 0; i < list.length; i++) {
        const row = list[i];
        const key =
          row != null &&
          row.reference != null &&
          String(row.reference) !== ""
            ? String(row.reference)
            : "_r" + r + "_i" + i;
        if (!seen[key]) {
          seen[key] = true;
          merged.push(row);
        }
      }
    }
    const primary = responses[0] != null ? responses[0] : {};
    const fallbackParams = {
      fields:
        parameters && parameters.fields ? parameters.fields : {},
      groupBy:
        parameters && parameters.groupBy ? parameters.groupBy : [],
    };
    const apiParams = primary && primary.parameters;
    const out = Object.assign({}, primary, {
      result: merged,
      results: merged,
      parameters:
        apiParams != null ? apiParams : fallbackParams,
    });
    this.globalReportSack = out;
    return out;
  }

  normalizeReportParameters(parameters: any): RequestParameters {
    const requestParameters = new RequestParameters();

    const datePipe = new DatePipe("en");
    const dates = parameters.fields.operationDate;
    const date1 = datePipe.transform(dates.value, "yyyy-MM-ddT00:00:00");
    const date2 = datePipe.transform(dates.value2, "yyyy-MM-ddT23:59:59");
    const reqDateParameter = new FieldRequestParameters(
      "operationDate",
      null,
      date1,
      date2,
    );
    requestParameters.fields.push(reqDateParameter);
    if (
      parameters.fields.amount !== undefined &&
      parameters.fields.amount !== null
    ) {
      const raw = parameters.fields.amount;
      let lower: string = null;
      let upper: string = null;
      if (typeof raw === "object" && raw !== null && !Array.isArray(raw)) {
        if (
          raw.lowerValue !== undefined &&
          raw.lowerValue !== null &&
          raw.lowerValue !== ""
        ) {
          lower = String(raw.lowerValue);
        }
        if (
          raw.upperValue !== undefined &&
          raw.upperValue !== null &&
          raw.upperValue !== ""
        ) {
          upper = String(raw.upperValue);
        }
      } else if (raw !== "" && raw !== null && typeof raw !== "object") {
        const s = String(raw);
        lower = s;
        upper = s;
      }
      if (lower !== null || upper !== null) {
        const reqAmount = new FieldRequestParameters(
          "amount",
          null,
          lower,
          upper,
        );
        requestParameters.fields.push(reqAmount);
      }
    }
    if (
      parameters.fields.organization !== undefined &&
      parameters.fields.organization !== null
    ) {
      const reqOrganizationParameter = new FieldRequestParameters(
        "organization",
        parameters.fields.organization.reference,
      );
      requestParameters.fields.push(reqOrganizationParameter);
    }

    if (
      parameters.fields.agency !== undefined &&
      parameters.fields.agency !== null
    ) {
      const reqAgencyParameter = new FieldRequestParameters(
        "agency",
        parameters.fields.agency.reference,
      );
      requestParameters.fields.push(reqAgencyParameter);
    }

    if (
      parameters.fields.callCenter !== undefined &&
      parameters.fields.callCenter !== null
    ) {
      const reqCallCenterParameter = new FieldRequestParameters(
        "callCenter",
        parameters.fields.callCenter.reference,
      );
      requestParameters.fields.push(reqCallCenterParameter);
    }

    if (
      parameters.fields.userOperatorPartner !== undefined &&
      parameters.fields.userOperatorPartner !== null
    ) {
      const reqUserOperatorPartnerParameter = new FieldRequestParameters(
        "userOperatorPartner",
        parameters.fields.userOperatorPartner.reference,
      );
      requestParameters.fields.push(reqUserOperatorPartnerParameter);
    }

    if (
      parameters.fields.userOperatorClk !== undefined &&
      parameters.fields.userOperatorClk !== null
    ) {
      const reqUserOperatorClkParameter = new FieldRequestParameters(
        "userOperatorClk",
        parameters.fields.userOperatorClk.reference,
      );
      requestParameters.fields.push(reqUserOperatorClkParameter);
    }

    if (
      parameters.fields.account !== undefined &&
      parameters.fields.account !== null
    ) {
      const reqAccountParameter = new FieldRequestParameters(
        "account",
        parameters.fields.account.reference,
      );
      requestParameters.fields.push(reqAccountParameter);
    }

    if (parameters.groupBy !== undefined && parameters.groupBy !== null) {
      for (const key in parameters.groupBy) {
        if (parameters.groupBy.hasOwnProperty(key)) {
          const reqUserOperatorPartnerParameter = new GroupByRequestParameters(
            parameters.groupBy[key].value,
          );
          requestParameters.groupBy.push(reqUserOperatorPartnerParameter);
        }
      }
    }

    if (parameters.orderBy !== undefined && parameters.orderBy !== null) {
      for (const key in parameters.orderBy) {
        if (key) {
          const reqUserOperatorPartnerParameter = new OrderByRequestParameters(
            parameters.orderBy[key].field,
            parameters.orderBy[key].value,
          );
          requestParameters.orderBy.push(reqUserOperatorPartnerParameter);
        }
      }
    }

    if (
      parameters.fields.operationType !== undefined &&
      parameters.fields.operationType !== null
    ) {
      for (const key in parameters.fields.operationType) {
        if (parameters.fields.operationType.hasOwnProperty(key)) {
          const reqUserOperationTypeParameter = new FieldRequestParameters(
            "operationType",
            parameters.fields.operationType[key].value,
          );
          requestParameters.fields.push(reqUserOperationTypeParameter);
        }
      }
    }
    if (parameters.fields.status) {
      const st = parameters.fields.status;
      if (Array.isArray(st)) {
        for (const key in st) {
          if (st.hasOwnProperty(key) && st[key] != null && st[key].value != null) {
            const reqUserStatusParameter = new FieldRequestParameters(
              "status",
              st[key].value,
            );
            requestParameters.fields.push(reqUserStatusParameter);
          }
        }
      } else if (
        typeof st === "object" &&
        st !== null &&
        st.value !== undefined &&
        st.value !== null &&
        st.value !== ""
      ) {
        if (st.value !== "ALL_CANCELLED_STATUSES") {
          const reqUserStatusParameter = new FieldRequestParameters(
            "status",
            st.value,
          );
          requestParameters.fields.push(reqUserStatusParameter);
        }
      }
    }

    requestParameters.limit = -1;

    return requestParameters;
  }
}



// WEBPACK FOOTER //
// ./src/app/report/reports/reports.service.ts